import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import backgroundImage from "../assets/membership-background.jpg";

const Membership = () => {


  // State for membership tiers dropdown (eligibility)
  const [openIndex, setOpenIndex] = useState(null);

  const toggleDropdown = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const tiers = [
    {
      name: "Aspire Membership",
      price: "FREE",
      benefits: [
        "Access to free leadership and professional development resources",
"Participation in community events and workshops", 
"Online and offline support from our network",
      ],
      eligibility: [
        "Open to individuals passionate about mentorship, leadership, and personal growth",
        "Must register and agree to community guidelines",
        "Commitment to engaging in at least one community initiative annually",
      ],
      buttonText: "Join Now",
      bgColor: "bg-white",
      textColor: "text-gray-700",
    },
    {
      name: "Empowered Membership",
      price: "₦75,000 / Year",
      benefits: [
        "Direct access to the Lead Mentor for guidance and mentorship",
        "Invitation to the Annual Leadership Summit",
        "Discounted rates for exclusive events and training sessions",
        "Priority access to career and business opportunities",
        "Exclusive networking events with industry leaders",
        "Official brand merchandise",
      ],
      eligibility: [
        "Must demonstrate significant progress in their career, business, or field of expertise",
        "Must have actively participated in at least one mentorship program",
        "Willingness to contribute back to the community through mentorship or volunteering",
        "Commitment to professional development and leadership",
      ],
      buttonText: "Join Now",
      bgColor: "bg-green-600 text-white shadow-lg scale-105",
      textColor: "text-white",
    },
    {
      name: "Premium Membership",
      price: "For Partners ONLY",
      benefits: [
        "Annual recognition for contributions to mentorship and leadership development",
        "Exclusive brand merchandise as a token of appreciation",
        "Promotion of their business, brand, or initiatives through our platform",
        "Special access to networking and VIP events",
        "Opportunity to collaborate on high-level mentorship and leadership programs",
      ],
      eligibility: [
        "Must be a sponsor, consultant, advisor, or major contributor to our mentorship programs",
        "Commitment to supporting mentorship and leadership development through financial, strategic, or advisory input",
        "Must align with the organization's values and long-term vision",
        "Willingness to engage in at least one strategic event or initiative annually",
      ],
      buttonText: "Be a Partner",
      bgColor: "bg-white",
      textColor: "text-gray-700",
    },
  ];

  // const testimonials = [
  //   {
  //     quote: "Joining the Mid-Career Professionals tier was a game changer for my career!",
  //     author: "Alice Johnson",
  //   },
  //   {
  //     quote: "The Executive Leaders membership provided unparalleled opportunities and mentorship.",
  //     author: "Michael Smith",
  //   },
  // ];

  return (
    <div className="container mx-auto px-6 py-16">
      {/* Enhanced Hero Section */}
      <motion.section
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ duration: 0.8 }}
  className="relative overflow-hidden text-white py-28 min-h-[60vh] flex items-center justify-center text-center rounded-3xl"
  style={{
    backgroundImage: `url(${backgroundImage})`, // Replace with your actual image path
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }}
>
  {/* Gradient overlay */}
  <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 via-green-700/90 to-green-800/90"></div>

  <motion.div
    initial={{ y: 50, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ delay: 0.2, duration: 0.8 }}
    className="z-10 px-4"
  >
    <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-green-100">
      Become a Member
    </h1>
    <p className="mt-4 text-xl md:text-2xl max-w-2xl mx-auto text-green-100">
      Gain access to premium resources, mentorship, and networking opportunities.
    </p>
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="mt-8 px-8 py-4 bg-white text-green-700 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
    >
      Get Started Today
    </motion.button>
  </motion.div>
  
  {/* Abstract background shapes - keeping them for additional depth */}
  <div className="absolute inset-0 overflow-hidden">
    <div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
    <div className="absolute -bottom-1/2 -left-1/4 w-96 h-96 bg-green-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
  </div>
</motion.section>

      {/* Membership Tiers Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="mt-24"
      >
        <h2 className="text-5xl font-bold text-center text-green-700 mb-16">
          <span className="relative">
            Membership Tiers
            <span className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-green-500 rounded-full"></span>
          </span>
        </h2>
        
        <div className="grid md:grid-cols-3 gap-8 px-4">
          {tiers.map((tier, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`p-8 rounded-2xl shadow-xl ${tier.bgColor} transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col`}
            >
              <div className="flex-1">
                <h2 className={`text-3xl font-bold ${tier.textColor} mb-4`}>{tier.name}</h2>
                <p className={`text-2xl font-semibold ${tier.textColor} mb-6`}>{tier.price}</p>
                <ul className="space-y-4">
                  {tier.benefits.map((benefit, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className={`text-lg ${tier.textColor} flex items-center`}
                    >
                      <span className="mr-2">✨</span> {benefit.replace('✅ ', '')}
                    </motion.li>
                  ))}
                </ul>
              </div>

              <div className="mt-8">
              <motion.button
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition ${
    index === 1
      ? "bg-yellow-500 text-white hover:bg-yellow-400"
      : "bg-green-600 text-white hover:bg-green-500"
  }`}
  onClick={() => window.open("https://forms.gle/4YMXNvyDujZvBdvh6", "_blank")}
>
  {tier.buttonText}
</motion.button>


                <button
                  onClick={() => toggleDropdown(index)}
                  className="flex justify-between items-center w-full text-lg font-semibold mt-6 hover:text-green-600 transition-colors"
                >
                  Eligibility Criteria 
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="h-5 w-5" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.ul
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`mt-4 space-y-3 ${tier.textColor}`}
                    >
                      {tier.eligibility.map((criterion, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className="flex items-center text-lg"
                        >
                          <span className="mr-2 text-green-500">•</span> {criterion}
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Testimonials Section */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mt-16"
      >
        <h2 className="text-4xl font-bold text-center text-green-700 mb-8">
          Testimonials
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-xl shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
            >
              <p className="text-gray-700 italic mb-4">
                "{testimonial.quote}"
              </p>
              <p className="font-semibold text-green-700">
                - {testimonial.author}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div> */}
    </div>
  );
};

export default Membership;
